import React, { HTMLProps, useEffect, useMemo, useRef, useState } from "react";
import ReactInputMask from "react-input-mask";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { FormikErrors, FormikTouched } from "formik";
import { IconBaseProps, IconType } from "react-icons";

import theme from "styles/theme";

import { Container, CorrectIcon, ErrorText, Eye, LabelText } from "./styles";

type TextFieldProps = HTMLProps<HTMLInputElement> & {
  LeadingIcon?: IconType | any;
  propsLeadingIcon?: IconBaseProps;
  TrailingIcon?: IconType | any;
  propsTrailingIcon?: IconBaseProps;
  errorMessage?: string | FormikErrors<any>;
  errorCondition?: boolean | FormikTouched<any>;
  borderHighlightColor?: string;
  isCorrect?: boolean;
  showChangeTextPassword?: boolean;
  mask?: string;
  inputMode?: string;
};

const TextField = ({
  LeadingIcon,
  propsLeadingIcon,
  TrailingIcon,
  propsTrailingIcon,
  label,
  errorMessage,
  name,
  errorCondition,
  placeholder,
  borderHighlightColor,
  isCorrect,
  showChangeTextPassword,
  disabled,
  pattern,
  ...rest
}: TextFieldProps): JSX.Element => {
  const inputRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isErrored, setIsErrored] = useState(false);
  const [showText, isShowText] = useState(false);
  const [isLabelActivated, setIsLabelActivated] = useState(false);

  useEffect(() => {
    setIsErrored(!!errorCondition);
  }, [errorCondition]);

  useEffect(() => {
    if (inputRef && inputRef.current) {
      const input = inputRef.current.getInputDOMNode();
      input.addEventListener("blur", () => {
        setIsFocused(false);
      });
      input.addEventListener("focus", () => {
        setIsFocused(true);
      });
    }
  }, [inputRef]);

  useEffect(() => {
    if (inputRef && inputRef.current) {
      const input = inputRef.current.getInputDOMNode();
      const inputValue = input ? input.value || "" : "";
      if (inputValue.length > 0) setIsLabelActivated(true);
      if (!isFocused && inputValue.length <= 0) setIsLabelActivated(false);
    }
  }, [rest, inputRef]);

  useEffect(() => {
    const input = inputRef.current.getInputDOMNode();
    const inputValue = input ? input.value || "" : "";
    if (isFocused || inputValue.length > 0) setIsLabelActivated(true);
    else setIsLabelActivated(false);
  }, [isFocused]);

  useEffect(() => {
    if (showChangeTextPassword) {
      const input = inputRef.current.getInputDOMNode();
      if (input && input.type === "password" && showText) input.type = "text";

      if (input && input.type === "text" && !showText) input.type = "password";
    }
  }, [showText, showChangeTextPassword]);

  const randomId = useMemo(() => {
    const text = label || "";
    return `${text.replace(/\s/g, "")}-${Math.floor(Math.random() * 100)}`;
  }, [label]);

  const LeftIcon = useMemo(() => {
    switch (typeof LeadingIcon) {
      case "function":
        return LeadingIcon();
      case "object":
        return LeadingIcon;
      default:
        return <LeadingIcon {...propsLeadingIcon} />;
    }
  }, [LeadingIcon]);

  const RightIcon = useMemo(() => {
    switch (typeof TrailingIcon) {
      case "function":
        return TrailingIcon();
      case "object":
        return TrailingIcon;
      default:
        return <TrailingIcon {...propsTrailingIcon} />;
    }
  }, [LeadingIcon]);

  return (
    <>
      <Container
        isFocused={isFocused}
        isErrored={isErrored}
        borderHighlightColor={borderHighlightColor}
        disabled={disabled}
      >
        {LeadingIcon && LeftIcon}
        <div>
          <LabelText
            labelActivated={isLabelActivated}
            id={`labelId-${randomId}`}
          >
            {label}
          </LabelText>
          <ReactInputMask
            name={name}
            ref={inputRef}
            type="text"
            aria-labelledby={`labelId-${randomId}`}
            placeholder={label ? "" : placeholder}
            aria-controls={`control-${randomId}`}
            aria-describedby={`control-${randomId}`}
            disabled={disabled}
            pattern={pattern || null}
            {...rest}
          />
        </div>
        {inputRef?.current?.value && isCorrect && <CorrectIcon size={20} />}
        {TrailingIcon && RightIcon}
        {showChangeTextPassword && (
          <Eye>
            {showText ? (
              <AiOutlineEye size={20} onClick={() => isShowText(!showText)} />
            ) : (
              <AiOutlineEyeInvisible
                size={20}
                onClick={() => isShowText(!showText)}
              />
            )}
          </Eye>
        )}
      </Container>
      {errorMessage && errorCondition && (
        <ErrorText aria-hidden="true" id={`control-${randomId}`}>
          {`${errorMessage}`}
        </ErrorText>
      )}
    </>
  );
};

TextField.defaultProps = {
  borderHighlightColor: theme.colors.lightBlue,
  isCorrect: null,
  showChangeTextPassword: false,
};

export default TextField;
